import { useEffect, useRef, useState } from "react";
import RealtimeKitClient from "@cloudflare/realtimekit";
import { RtkMeeting } from "@cloudflare/realtimekit-react-ui";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Initializes a Cloudflare RealtimeKit client from a per-participant authToken
 * and renders the prebuilt RtkMeeting UI (setup/green-room screen, stage,
 * controls, screen-share, recording, leave). Audio-only experiences pass
 * defaults={{ audio: true, video: false }}.
 */
export function RealtimeMeeting({
  authToken,
  defaults = { audio: true, video: true },
  onLeave,
  onMeetingReady,
  className = "",
}) {
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(null);
  const meetingRef = useRef(null);
  const startedRef = useRef(false);
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;
  const onReadyRef = useRef(onMeetingReady);
  onReadyRef.current = onMeetingReady;

  // Initialize the RealtimeKit client exactly once. Client.init() does NOT allow
  // concurrent calls, so we guard with a ref that survives React StrictMode's
  // double-invocation of effects in development.
  useEffect(() => {
    if (!authToken) return undefined;
    if (startedRef.current) {
      if (meetingRef.current) {
        setMeeting(meetingRef.current);
        try { if (onReadyRef.current) onReadyRef.current(meetingRef.current); } catch (e) { /* noop */ }
      }
      return undefined;
    }
    startedRef.current = true;
    RealtimeKitClient.init({ authToken, defaults })
      .then((m) => {
        meetingRef.current = m;
        setMeeting(m);
        try { if (onReadyRef.current) onReadyRef.current(m); } catch (e) { /* noop */ }
        try {
          m.self.on("roomLeft", () => { if (onLeaveRef.current) onLeaveRef.current(); });
        } catch (e) { /* noop */ }
      })
      .catch((e) => setError(e?.message || "Could not start the session"));
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Leave the meeting only when the component truly unmounts.
  useEffect(() => {
    return () => {
      const m = meetingRef.current;
      meetingRef.current = null;
      if (m) { try { m.leave(); } catch (e) { /* noop */ } }
    };
  }, []);

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card p-8 text-center ${className}`}
        data-testid="realtime-meeting-error"
      >
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <p className="font-semibold text-foreground">We couldn&apos;t start the session</p>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <p className="text-xs text-muted-foreground">
          Please allow camera &amp; microphone access and use a modern Chrome, Edge, or Safari browser.
        </p>
        {onLeave && (
          <Button variant="outline" size="sm" onClick={onLeave} data-testid="realtime-meeting-error-back">
            Go back
          </Button>
        )}
      </div>
    );
  }

  if (!meeting) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card ${className}`}
        data-testid="realtime-meeting-loading"
      >
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Connecting to the studio…</p>
      </div>
    );
  }

  return (
    <div
      className={`overflow-hidden rounded-xl border border-border bg-black ${className}`}
      data-testid="realtime-meeting"
    >
      <RtkMeeting meeting={meeting} style={{ height: "100%", width: "100%" }} />
    </div>
  );
}

export default RealtimeMeeting;
